import React from 'react';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import ClassificationTags from './ClassificationTags';

export interface KnowledgeLibraryClassificationFilterValue {
    id: string;
    name: string;
}

export type ClassificationFilterSortDirection = 'asc' | 'desc';

interface KnowledgeLibraryClassificationFilterProps {
    values: KnowledgeLibraryClassificationFilterValue[];
    selectedIds: Set<string> | null;
    onChange: (selectedIds: Set<string> | null) => void;
    sortDirection: ClassificationFilterSortDirection;
    onSortDirectionChange: (sortDirection: ClassificationFilterSortDirection) => void;
}

const { useState, useEffect, useRef, useCallback, useMemo } = React;

const KnowledgeLibraryClassificationFilter: React.FC<KnowledgeLibraryClassificationFilterProps> = ({
    values,
    selectedIds,
    onChange,
    sortDirection,
    onSortDirectionChange
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [draftCheckedIds, setDraftCheckedIds] = useState<Set<string>>(() => new Set());

    const allIds = useMemo(() => values.map(value => value.id), [values]);
    const isFilterActive = selectedIds !== null;

    const openPopup = useCallback(() => {
        const initialChecked = selectedIds ? new Set(selectedIds) : new Set(allIds);
        setDraftCheckedIds(initialChecked);
        setIsOpen(true);
    }, [allIds, selectedIds]);

    const closePopup = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleTriggerClick = useCallback(() => {
        if (isOpen) {
            closePopup();
            return;
        }

        openPopup();
    }, [closePopup, isOpen, openPopup]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                closePopup();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closePopup();
            }
        };

        document.addEventListener('mousedown', handlePointerDown, true);
        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown, true);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [closePopup, isOpen]);

    const areAllChecked = allIds.length > 0 && allIds.every(id => draftCheckedIds.has(id));
    const areSomeChecked = allIds.some(id => draftCheckedIds.has(id));
    const isSelectAllIndeterminate = areSomeChecked && !areAllChecked;

    const handleSelectAllChange = useCallback(() => {
        setDraftCheckedIds(areAllChecked ? new Set() : new Set(allIds));
    }, [allIds, areAllChecked]);

    const handleSelectedTagsChange = useCallback((selectedTagIds: string[]) => {
        setDraftCheckedIds(new Set(selectedTagIds));
    }, []);

    const handleClearFilter = useCallback(() => {
        onChange(null);
        closePopup();
    }, [closePopup, onChange]);

    const handleApply = useCallback(() => {
        const selectedIdsToApply = new Set(draftCheckedIds);
        const allChecked = selectedIdsToApply.size === allIds.length && allIds.every(id => selectedIdsToApply.has(id));

        if (allChecked) {
            onChange(null);
        } else {
            onChange(selectedIdsToApply);
        }

        closePopup();
    }, [allIds, closePopup, draftCheckedIds, onChange]);

    const handleCancel = useCallback(() => {
        closePopup();
    }, [closePopup]);

    const triggerClassName = isFilterActive
        ? 'kb-classification-filter-trigger active'
        : 'kb-classification-filter-trigger';

    return (
        <div className="kb-classification-filter" ref={containerRef}>
            <button
                type="button"
                className={triggerClassName}
                title={InovxLoader.t('facility:knowledgeBook.filterByClassification')}
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={handleTriggerClick}
            >
                <span className="e-icons e-filter" aria-hidden="true"></span>
                <span className="kb-classification-filter-label">{InovxLoader.t('facility:knowledgeBook.classificationTags')}</span>
                <span className="e-icons e-chevron-down-fill kb-classification-filter-caret" aria-hidden="true"></span>
            </button>

            {isOpen && (
                <div className="kb-classification-filter-popup" role="dialog">
                    <div className="kb-classification-filter-actions">
                        <button
                            type="button"
                            className={sortDirection === 'asc' ? 'kb-classification-filter-action active' : 'kb-classification-filter-action'}
                            onClick={() => onSortDirectionChange('asc')}
                        >
                            <span className="e-icons e-sort-ascending" aria-hidden="true"></span>
                            <span>{InovxLoader.t('facility:knowledgeBook.sortAtoZ')}</span>
                        </button>
                        <button
                            type="button"
                            className={sortDirection === 'desc' ? 'kb-classification-filter-action active' : 'kb-classification-filter-action'}
                            onClick={() => onSortDirectionChange('desc')}
                        >
                            <span className="e-icons e-sort-descending" aria-hidden="true"></span>
                            <span>{InovxLoader.t('facility:knowledgeBook.sortZtoA')}</span>
                        </button>
                        <button
                            type="button"
                            className="kb-classification-filter-action"
                            disabled={!isFilterActive}
                            onClick={handleClearFilter}
                        >
                            <span className="e-icons e-filter-clear" aria-hidden="true"></span>
                            <span>{InovxLoader.t('facility:knowledgeBook.clearFilter')}</span>
                        </button>
                    </div>

                    <div className="kb-classification-filter-list">
                        <label className="kb-classification-filter-option kb-classification-filter-select-all">
                            <CheckBoxComponent
                                checked={areAllChecked}
                                indeterminate={isSelectAllIndeterminate}
                                label={InovxLoader.t('facility:knowledgeBook.selectAll')}
                                change={handleSelectAllChange}
                            />
                        </label>
                        <div className="kb-classification-filter-tree taxonomy-wrapper">
                            {values.length === 0 ? (
                                <div className="kb-classification-filter-empty">
                                    {InovxLoader.t('facility:knowledgeBook.noMatchingClassificationTags')}
                                </div>
                            ) : (
                                <ClassificationTags
                                    selectedTags={Array.from(draftCheckedIds)}
                                    onSelectedTagsChange={handleSelectedTagsChange}
                                    restrictToCurrentUserPermissions={true}
                                    allowViewOnlyTagSelection={true}
                                    allowParentChildSelection={true}
                                />
                            )}
                        </div>
                    </div>

                    <div className="kb-classification-filter-footer">
                        <button
                            type="button"
                            className="e-btn e-primary kb-classification-filter-ok"
                            onClick={handleApply}
                        >
                            {InovxLoader.t('common:form.ok')}
                        </button>
                        <button
                            type="button"
                            className="e-btn e-flat kb-classification-filter-cancel"
                            onClick={handleCancel}
                        >
                            {InovxLoader.t('common:form.cancel')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KnowledgeLibraryClassificationFilter;
